import { CartAddEvent } from '@theme/events';

const roots = document.querySelectorAll('[data-em-choice]');

for (const root of roots) {
  if (!(root instanceof HTMLElement) || root.dataset.ready === 'true') continue;
  root.dataset.ready = 'true';
  document.body.classList.add('em-choice-present');

  const trigger = root.querySelector('[data-em-choice-trigger]');
  const backdrop = root.querySelector('[data-em-choice-backdrop]');
  const dialog = root.querySelector('[data-em-choice-dialog]');
  const closeButton = root.querySelector('[data-em-choice-close]');
  const status = root.querySelector('[data-em-choice-status]');
  const addButtons = [...root.querySelectorAll('[data-em-choice-add]')];
  const nativeBuy = root.dataset.delayUntilNative === 'true'
    ? document.querySelector('[data-emstight-primary-cta]')
    : null;
  const homeHero = root.dataset.delayUntilHero === 'true'
    ? document.querySelector('.em-home__hero')
    : null;
  let previousFocus = null;
  let isAdding = false;

  if (!(trigger instanceof HTMLButtonElement) || !(backdrop instanceof HTMLButtonElement) || !(dialog instanceof HTMLElement)) {
    continue;
  }

  const setTriggerVisibility = (visible) => {
    root.dataset.visible = visible ? 'true' : 'false';
    trigger.setAttribute('aria-hidden', visible ? 'false' : 'true');
    trigger.tabIndex = visible ? 0 : -1;
  };

  const activationTarget = nativeBuy instanceof HTMLElement ? nativeBuy : homeHero;
  // Preview 14 Oct 2026: on phones the Face Mask bar also shows before the shopper reaches
  // the main Add to bag button. It still steps aside whenever that button is on screen.
  const showBeforeNative = () => activationTarget === nativeBuy && window.matchMedia('(max-width: 749px)').matches;
  if (activationTarget instanceof HTMLElement && 'IntersectionObserver' in window) {
    setTriggerVisibility(false);
    const visibilityObserver = new IntersectionObserver(([entry]) => {
      setTriggerVisibility(!entry.isIntersecting && (showBeforeNative() || entry.boundingClientRect.bottom < 0));
    }, { threshold: 0 });
    visibilityObserver.observe(activationTarget);
  } else if (activationTarget instanceof HTMLElement) {
    // Older webviews: never obscure the homepage hero or the Face Mask buy zone.
    const updatePosition = () => {
      const rect = activationTarget.getBoundingClientRect();
      setTriggerVisibility(rect.bottom < 0 || (showBeforeNative() && rect.top > window.innerHeight));
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, { passive: true });
  } else {
    setTriggerVisibility(true);
  }

  const getFocusable = () =>
    [...dialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter(
      (element) => element instanceof HTMLElement && !element.hasAttribute('hidden') && element.offsetParent !== null
    );

  const openChooser = () => {
    previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : trigger;
    backdrop.hidden = false;
    dialog.hidden = false;
    root.dataset.open = 'true';
    trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('em-choice-lock');
    requestAnimationFrame(() => {
      if (closeButton instanceof HTMLElement) closeButton.focus();
    });
  };

  const closeChooser = ({ restoreFocus = true } = {}) => {
    if (isAdding) return;
    backdrop.hidden = true;
    dialog.hidden = true;
    root.dataset.open = 'false';
    trigger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('em-choice-lock');
    if (restoreFocus) (previousFocus || trigger).focus();
  };

  const trapFocus = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeChooser();
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = getFocusable();
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const openNativeCart = async () => {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (!(cartDrawer instanceof HTMLElement)) return;
    await customElements.whenDefined('cart-drawer-component');
    if (typeof cartDrawer.open === 'function') cartDrawer.open();
  };

  const addToCart = async (button) => {
    if (!(button instanceof HTMLButtonElement) || isAdding || button.disabled) return;
    const variantId = button.dataset.variantId;
    const productId = button.dataset.productId;
    const productTitle = button.dataset.productTitle || 'Product';
    const label = button.querySelector('[data-em-choice-add-label]');
    if (!variantId || !productId || !(label instanceof HTMLElement)) return;

    isAdding = true;
    button.dataset.state = 'loading';
    button.setAttribute('aria-busy', 'true');
    const originalLabel = label.textContent || 'Add';
    label.textContent = 'Adding';
    if (status instanceof HTMLElement) status.textContent = `Adding ${productTitle} to your bag.`;

    try {
      const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : '')
        .filter(Boolean);
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      if (sectionIds.length) formData.append('sections', [...new Set(sectionIds)].join(','));
      formData.append('sections_url', window.location.pathname);

      const addResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });
      const addResult = await addResponse.json();
      if (!addResponse.ok || addResult.status) {
        throw new Error(addResult.description || addResult.message || 'This item could not be added.');
      }

      const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) throw new Error('Your bag could not be refreshed.');
      const cart = await cartResponse.json();

      button.dataset.state = 'added';
      label.textContent = 'Added';
      if (status instanceof HTMLElement) status.textContent = `${productTitle} added to your bag.`;

      document.dispatchEvent(
        new CartAddEvent(cart, `em-choice-${variantId}`, {
          source: 'emstight-sticky-chooser',
          itemCount: Number(cart.item_count) || 1,
          productId,
          variantId,
          sections: addResult.sections || {},
        })
      );

      setTimeout(async () => {
        isAdding = false;
        closeChooser({ restoreFocus: false });
        await openNativeCart();
        button.removeAttribute('aria-busy');
        button.dataset.state = 'idle';
        label.textContent = originalLabel;
      }, 420);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'This item could not be added.';
      isAdding = false;
      button.removeAttribute('aria-busy');
      button.dataset.state = 'error';
      label.textContent = 'Try again';
      if (status instanceof HTMLElement) status.textContent = message;
      setTimeout(() => {
        button.dataset.state = 'idle';
        label.textContent = originalLabel;
      }, 3000);
    }
  };

  trigger.addEventListener('click', openChooser);
  backdrop.addEventListener('click', () => closeChooser());
  closeButton?.addEventListener('click', () => closeChooser());
  dialog.addEventListener('keydown', trapFocus);
  for (const button of addButtons) {
    button.addEventListener('click', () => addToCart(button));
  }
}
